"use client";
import Buttons from "@/components/Buttons";
import InputSearchHeader from "@/components/Header/AppBarDefault/component/Search";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import UseAuth from "@/hooks/useAuth";
import useDeviceDetect from "@/hooks/useMobile";
import { setCurrencyLocal } from "@/lib/features/authSlice";
import { setErrorMess, setLoading } from "@/lib/features/statusSlice";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { currencyTypes } from "@/models/general";
import { GetReports, Report } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import authApiService from "@/services/main/UserServices";
import { KeyboardArrowDown } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import "@styles/Header/style.css";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ButtonChangeCurrency from "./component/ButtonChangeCurrency";
import LoginButton from "./component/LoginButton";
import NavBar from "./component/NavBar";
import classes from "./styles.module.scss";

import ButtonAi from "@/components/Header/AppBarDefault/component/ButtonAi";
import { useUserQuery } from "@/hooks/Query/useUserQuery";
import UseDebounceSearch from "@/hooks/useDebounceSearch";
import { setCookieSSR } from "@/utils/auth";
import { useTranslations } from "next-intl";
import ChangeLanguage from "./component/ChangeLanguage";
interface AppBarDefaultProps {
  locale?: string;
  currency?: string;
}

export const AppBarDefault = (props: AppBarDefaultProps) => {
  const { locale, currency } = props;
  const queryClient = useQueryClient();
  const { data: user } = useUserQuery({
    options: {
      enabled: false,
    },
  });
  const isLoggedIn = !!user;
  const isMobile = useDeviceDetect();
  const t = useTranslations("messages");

  const drawerPages = useMemo(
    () => [
      {
        originalName: "pricing",
        name: t("common_pricing"),
        href: APP_ROUTES(locale as any).PRICING.to,
      },
      {
        originalName: "faq",
        name: t("common_FAQ"),
        href: APP_ROUTES(locale as any).FAQ.to,
      },
    ],
    [t, locale],
  );
  const yourReports = useMemo(
    () => [
      {
        icon: image.iconFavorite,
        name: t("home_favorite"),
        url: APP_ROUTES(locale as any).FAVORITE.to,
      },
      {
        icon: image.iconReport,
        name: t("common_purchased_reports"),
        url: APP_ROUTES(locale as any).PURCHASED_REPORTS.to,
      },
      {
        icon: image.iconBookmark,
        name: t("common_saved_dashboards"),
        url: APP_ROUTES(locale as any).SAVED_DASHBOARD.to,
      },
    ],
    [t, locale],
  );

  const [localCurrency, setLocalCurrency] = useState(props.currency || "USD");
  const dispatch = useAppDispatch();
  const { logout } = UseAuth();
  const router = useRouter();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [anchorElCurrency, setAnchorElCurrency] = useState<null | HTMLElement>(
    null,
  );
  const [keyword, setKeyword] = useState<string>("");
  const searchValue = UseDebounceSearch(keyword, 600);
  const [reports, setReports] = useState<null | Report[]>([]);
  const [focusSearch, setFocusSearch] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const { currencyLocal } = useAppSelector((state) => state.auth);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpenDrawer(newOpen);
  };

  useEffect(() => {
    if (currencyLocal) {
      setLocalCurrency(currencyLocal);
    }
  }, [currencyLocal]);

  const handleRenderDawerPage = useCallback(() => {
    return drawerPages
      ?.filter(
        (item) =>
          !(
            user?.license &&
            !user?.license?.isOutOfDate &&
            item.originalName === "pricing"
          ),
      )
      .map((item, index) => (
        <div className="text-center" key={index}>
          <a
            target={"_blank"}
            onClick={toggleDrawer(false)}
            className="no-underline !text-[0.9rem] !font-bold cursor-pointer text-cimigo-blue hover:!text-cimigo-green"
            href={item?.href}
          >
            {item?.name}
          </a>
        </div>
      ));
  }, [drawerPages, user]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectItem = (url: string) => {
    router.push(url);
  };
  const btnLogout = () => {
    dispatch(setLoading(true));
    queryClient.clear();
    setAnchorEl(null);
    toggleDrawer(false);
    logout();
  };

  const fetchData = (keyword?: string) => {
    const _params: GetReports = {
      page: 1,
      take: 5,
      keyword: keyword ?? "",
      sortedField: "publishedDate",
      isDescending: true,
    };

    ReportServices.findAll(_params)
      .then((res) => {
        setReports(res.data);
      })
      .catch((e) => dispatch(setErrorMess(e)))
      .finally(() => dispatch(setLoading(false)));
  };
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  useEffect(() => {
    if (focusSearch) {
      fetchData(searchValue);
    }
  }, [focusSearch, searchValue]);

  const changeCurrency = async (currency: string) => {
    setAnchorElCurrency(null);
    queryClient.setQueryData(["config"], (oldData: any) => {
      return {
        ...oldData,
        currency: currency,
      };
    });
    if (!isLoggedIn) return;
    await authApiService.changeCurrency(currency).then(() => {
      queryClient.setQueryData(["user"], (oldData: any) => {
        return {
          ...oldData,
          currency: currency,
        };
      });
    });
  };

  const changeCurrencyLocal = async (currency: string) => {
    setAnchorElCurrency(null);
    setLocalCurrency(currency);
    dispatch(setCurrencyLocal(currency));
    setCookieSSR("currency", currency);
    queryClient.setQueryData(["config"], (oldData: any) => {
      return {
        ...oldData,
        currency: currency,
      };
    });
  };

  const handleShowCurrency = useMemo(() => {
    return currencyTypes?.find((it) => it.id === (user?.currency ?? "USD"))
      ?.name;
  }, [user?.currency]);
  const handleShowCurrencyMobile = useMemo(() => {
    return currencyTypes?.find((it) => it.id === (user?.currency ?? "USD"))
      ?.subName;
  }, [user?.currency]);
  const handleShowCurrencyLocal = useMemo(() => {
    return currencyTypes?.find((it) => it.id === (localCurrency ?? "USD"))
      ?.name;
  }, [localCurrency]);
  const handleShowCurrencyLocalMobile = useMemo(() => {
    return currencyTypes?.find((it) => it.id === (localCurrency ?? "USD"))
      ?.subName;
  }, [localCurrency]);
  const handleRedirectToLogin = () => {
    const currentUrl =
      typeof window !== "undefined"
        ? window.location.pathname +
          window.location.search +
          window.location.hash
        : "";
    router.push(
      `${APP_ROUTES(locale as any).LOGIN.to}?redirect=${encodeURIComponent(currentUrl)}`,
    );
  };
  return (
    <header className="shadow-sm appBar bg-white top-0 w-full z-[99]">
      <Toolbar className="justify-between md:py-3 lg:!px-4 xl:!px-16 md:gap-[26px] max-h-[72px]">
        <Box className="flex-1 flex xl:gap-7 gap-1 md:gap-4 h-12 items-center">
          <Box
            className=" flex items-center md:hidden p-[2px] h-6 border-none"
            onClick={toggleDrawer(true)}
          >
            <MenuIcon className="!w-6 !h-6" />
          </Box>
          <Box className="flex-none focus-visible:outline-0 md:pr-0 lg:mr-0.5">
            <a
              className="flex items-center h-full cursor-pointer focus-visible:outline-0"
              href={APP_ROUTES(locale as any).HOME.to}
            >
              <Image
                src={image.cimigoLogo}
                className="iconLogo focus-visible:outline-0"
                alt="cimigoLogo"
                height={48}
              />
            </a>
          </Box>
          <NavBar user={user} locale={locale} />
          <Box className="flex-auto boxSearch">
            <InputSearchHeader
              className={clsx("inputSeachMobile", {
                "relative rounded-tl-[30px] rounded-tr-[30px] rounded-bl-none rounded-br-none":
                  focusSearch,
              })}
              onBlur={() => {
                setTimeout(() => {
                  setFocusSearch(false);
                }, 400);
              }}
              onFocus={() => {
                setFocusSearch(true);
              }}
              focusSearch={focusSearch}
              onChange={onSearch}
              reports={reports ?? undefined}
              handleSelectItem={handleSelectItem}
              placeholder={t("common_search_reports")}
              translation_placeholder_key={t("common_search_reports")}
              onClose={() => setFocusSearch(false)}
            />
          </Box>
          <ButtonAi isMobile={isMobile} />
        </Box>
        <Box className=" flex justify-end items-center h-full  lg:gap-8 md:gap-4 itemRight">
          <Box className="hidden md:flex lg:gap-3 md:gap-4 items-center gap-4">
            <div className="flex items-center justify-center h-auto">
              <ChangeLanguage locale={locale} user={user} />
            </div>
            {user ? (
              <ButtonChangeCurrency
                changeCurrency={changeCurrency}
                anchorElCurrency={anchorElCurrency}
                setAnchorElCurrency={setAnchorElCurrency}
                handleShowCurrency={handleShowCurrency}
                handleShowCurrencyMobile={handleShowCurrencyMobile}
                currency={user?.currency}
              />
            ) : (
              <ButtonChangeCurrency
                changeCurrency={changeCurrencyLocal}
                anchorElCurrency={anchorElCurrency}
                setAnchorElCurrency={setAnchorElCurrency}
                handleShowCurrency={handleShowCurrencyLocal}
                handleShowCurrencyMobile={handleShowCurrencyLocalMobile}
                currency={localCurrency}
              />
            )}
            <LoginButton
              locale={locale}
              organization={user?.organization}
              open={open}
              setAnchorEl={setAnchorEl}
              user={user}
              yourReports={yourReports}
              handleClick={handleClick}
              anchorEl={anchorEl}
              btnLogout={btnLogout}
              handleClose={handleClose}
              isLogin={isLoggedIn}
            />
          </Box>
          <Box
            className="md:hidden flex justify-center w-full ml-2"
            translation-key={"common_search_reports"}
          >
            <InputSearchHeader
              className={"!py-[6px] !px-2"}
              onBlur={() => {
                setTimeout(() => {
                  setFocusSearch(false);
                }, 200);
              }}
              onFocus={() => {
                setFocusSearch(true);
              }}
              onClose={() => setFocusSearch(false)}
              onChange={onSearch}
              handleSelectItem={handleSelectItem}
              placeholder={t("common_search_reports")}
            />
          </Box>
        </Box>
      </Toolbar>
      {/* <Box className="px-3 bg-white">
          <Box className="containerMobile">
            <InputSearchHeader
              className={clsx("inputSeachMobile", {
                "relative rounded-tl-[30px] rounded-tr-[30px] rounded-bl-none rounded-br-none":
                  reports?.length && focusSearch,
              })}
              onBlur={() => {
                setTimeout(() => {
                  setFocusSearch(false);
                }, 200);
              }}
              onFocus={() => {
                setFocusSearch(true);
              }}
              focusSearch={focusSearch}
              onChange={onSearch}
              reports={reports ?? undefined}
              handleSelectItem={handleSelectItem}
              placeholder="Search reports"
            />
          </Box>
        </Box> */}
      <Drawer
        open={openDrawer}
        onClose={toggleDrawer(false)}
        sx={{
          ".MuiPaper-root": {
            width: 288,
          },
        }}
      >
        <Box className="flex justify-between items-center p-2 mx-2">
          <Box className="flex justify-center items-center gap-2 p-2">
            {!isLoggedIn ? (
              <div className="text-center">
                <Button
                  className={
                    "!bg-ciBlue_light_4 !py-2 !px-4 hover:cursor-pointe !rounded btn_login"
                  }
                  onClick={() => handleRedirectToLogin()}
                >
                  <Typography
                    className={"!font-medium normal-case !text-sm/[24px]"}
                    translation-key="common_login"
                  >
                    {t("common_login")}
                  </Typography>
                </Button>
              </div>
            ) : null}
            <LoginButton
              organization={user?.organization}
              toggleDrawer={setOpenDrawer}
              open={open}
              setAnchorEl={setAnchorEl}
              user={user}
              yourReports={yourReports}
              handleClick={handleClick}
              anchorEl={anchorEl}
              btnLogout={btnLogout}
              handleClose={handleClose}
              isLogin={isLoggedIn}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="grid gap-4 mt-4 flex-1">
          <Box className="grid gap-4">
            <Box className="flex flex-col gap-4">
              {/* <div className="text-center" key={"cimigo"}>
                  <Link
                    onClick={toggleDrawer(false)}
                    className="no-underline !text-[0.9rem] !font-bold cursor-pointer text-cimigo-blue hover:!text-cimigo-green"
                    href={APP_ROUTES.HOME.to}
                    translation-key="common_home"
                  >
                    {t("common_home")}
                  </Link>
                </div> */}
              {handleRenderDawerPage()}
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className="flex justify-between items-center p-2 mx-2">
          <div className="flex items-center justify-center">
            <ChangeLanguage locale={locale} user={user} />
          </div>
          <Box className="flex justify-center items-center gap-2 p-2">
            {isLoggedIn ? (
              <Box className={"flex md:hidden"}>
                <Buttons
                  className={classes.btnChangeLang}
                  padding="7px 10px"
                  onClick={(e) => setAnchorElCurrency(e.currentTarget)}
                  endIcon={
                    user?.currency ? (
                      <KeyboardArrowDown
                        sx={{
                          transform: Boolean(anchorElCurrency)
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    ) : null
                  }
                >
                  <span className={classes.desktop}>{handleShowCurrency}</span>
                  <span className={classes.mobile}>
                    {handleShowCurrencyMobile}
                  </span>
                </Buttons>
                <Menu
                  disableScrollLock={true}
                  anchorEl={anchorElCurrency}
                  open={Boolean(anchorElCurrency)}
                  onClose={() => setAnchorElCurrency(null)}
                  classes={{
                    paper: clsx(classes.menuProfile, classes.menuLang),
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      marginLeft: "-8px",
                    },
                  }}
                >
                  {currencyTypes.map((it) => (
                    <MenuItem
                      key={it.id}
                      onClick={() => changeCurrency(it.id)}
                      className={clsx(classes.itemAciton, {
                        [classes.active]: it.id === (user?.currency ?? "USD"),
                      })}
                    >
                      <p className={classes.itName}>{it.name}</p>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <div className={"flex md:hidden"}>
                <Buttons
                  className={classes.btnChangeLang}
                  padding="7px 10px"
                  onClick={(e) => setAnchorElCurrency(e.currentTarget)}
                  endIcon={
                    localCurrency ? (
                      <KeyboardArrowDown
                        sx={{
                          transform: Boolean(anchorElCurrency)
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    ) : null
                  }
                >
                  <span className={classes.desktop}>
                    {handleShowCurrencyLocal}
                  </span>
                  <span className={classes.mobile}>
                    {handleShowCurrencyLocalMobile}
                  </span>
                </Buttons>
                <Menu
                  anchorEl={anchorElCurrency}
                  open={Boolean(anchorElCurrency)}
                  onClose={() => setAnchorElCurrency(null)}
                  classes={{
                    paper: clsx(classes.menuProfile, classes.menuLang),
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      marginLeft: "-15px",
                    },
                  }}
                >
                  {currencyTypes.map((it) => (
                    <MenuItem
                      key={it.id}
                      onClick={() => changeCurrencyLocal(it.id)}
                      className={clsx(classes.itemAciton, {
                        [classes.active]: it.id === (localCurrency ?? "USD"),
                      })}
                    >
                      <p className={classes.itName}>{it.name}</p>
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )}
          </Box>
        </Box>
      </Drawer>
    </header>
  );
};
